import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { debounceTime, distinctUntilChanged, Observable, Subscription, take } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { IInventoryFilter, IInventoryFilterRequest, IProduct } from '../../models/products.models';
import { AddProductSuccess, DeleteProduct, DeleteProductSuccess, FetchProducts, ImproveVariantsProductSuccess, SetInventoryFilter, exportProducts, SetInventorySortBy, UpdateProductSuccess, DeleteProductsSuccess, DeleteProducts } from '../../store/produits.actions';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { SetCurrentElementId } from 'src/app/shared/store/shared.actions';
import { DATE_FORMAT, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/app/config/global.config';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { MatDialog } from '@angular/material/dialog';
import { encodeFilterObject, parseFilterRequest, parseInventoryFilterRequest, refillFilter } from 'src/app/shared/utils/helpers';
import { Actions, ofType } from '@ngrx/effects';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { NavigationService } from 'src/app/shared/router.service';
import { FilterDialogComponent } from 'src/app/features/produits/components/filter-dialog/filter-dialog.component';
import { selectHiddenFieldsForModule } from 'src/app/core/store/field-visibility/field-visibility.selectors';
import { ITableColumnSetting, TablePreferencesService } from 'src/app/shared/table-preferences.service';
import { hasProductStatus, productStatusI18nKey, productStatusTone } from '../../utils/product-status';
import { StatusBadgeTone } from 'src/app/ui/status-badge/status-badge.component';
import { SelectionController } from 'src/app/shared/utils/selection-controller';

type ListColumnKey =
  | 'image' | 'name' | 'erp_id' | 'sku' | 'ean' | 'brand' | 'categories' | 'variants'
  | 'stock' | 'catalogs' | 'sales_price' | 'performance' | 'last_order' | 'status';

interface IListColumnConfig {
  label: string;
  visible: boolean;
  locked?: boolean;
}

@Component({
  selector: 'app-list-produits',
  templateUrl: './list-produits.component.html',
  styleUrls: ['./list-produits.component.scss']
})
export class ListProduitsComponent implements OnInit {
  expandedIndex: number = -1;
  page: IPageCount = {
    size: DEFAULT_PAGE_SIZE,
    number: 1
  }
  DATE_FORMAT = DATE_FORMAT;

  filter: IInventoryFilterRequest | undefined = undefined;
  key: string | undefined = undefined;
  products$: Observable<IProduct[]> = this.store.select((state)=> state.products.products);
  products: IProduct[] = [];
  pagination$: Observable<IPagination | null> = this.store.select((state) => state.products.metadata);

  /**
   * Maps a list-view column key to the backend role/field-visibility key for the
   * product module. Only columns gated by a hideable field are listed; everything
   * else is governed solely by the per-user column preference (the gear menu).
   */
  private readonly COLUMN_TO_FIELD_KEY: Partial<Record<ListColumnKey, string>> = {
    image: 'image',
    brand: 'brand',
    categories: 'categories',
    stock: 'stock',
    sales_price: 'sales_price',
  };
  private hiddenProductFields = new Set<string>();

  private static readonly THIS_TABLE_KEY = 'inventory_list_table';

  /** Role-allowed columns passed to the <ui-table-column-settings> popover. */
  pickerColumns: ITableColumnSetting[] = [];
  /** Current grid-track order (excludes 'image', which lives in the select cell). */
  gridColumnKeys: ListColumnKey[] = [];
  /** Memoized 1-based grid `order` per visible column (select cell = 0). */
  private gridOrderIndex: Partial<Record<ListColumnKey, number>> = {};

  columns: Record<ListColumnKey, IListColumnConfig> = {
    image:       { label: 'inventory.img', visible: true },
    name:        { label: 'inventory.product', visible: true, locked: true },
    erp_id:      { label: 'inventory.erp_id', visible: true },
    sku:         { label: 'inventory.sku', visible: true },
    ean:         { label: 'inventory.ean', visible: true },
    brand:       { label: 'brands.brands', visible: true },
    categories:  { label: 'Categories.categories', visible: true },
    variants:    { label: 'Catalogs.variant', visible: true },
    stock:       { label: 'inventory.stock', visible: true },
    catalogs:    { label: 'Catalogs.catalogs', visible: true },
    sales_price: { label: 'Catalogs.retail_price', visible: true },
    performance: { label: 'inventory.performance', visible: true },
    last_order:  { label: 'agent.listing.last_order', visible: true },
    status:      { label: 'common.status', visible: true },
  };
  isAppLoading$ = this.store.select((state) => state.shared.isAppLoading);
  timestamp = new Date().getTime();
  subscriptions: Subscription[] = [];
  activeSortBy: string  = 'id';
  activeSortMode: string = 'desc';
  baseUrl = environment.BASE_URL_API;
  failedImages = new Set<string>();
  unparsedFilter:IInventoryFilter | null = null;
  module:string = 'inventory';
  searchInput = new FormControl('');
  maxPerformanceProduct!: IProduct;
  isReturningFromDetails: boolean = false;
  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<IProduct>((product) => product.id!);
  constructor(
    private router: Router,
    private store: Store<AppState>,
    public dialog: MatDialog,
    public actions$: Actions,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute,
    private navigationService: NavigationService,
    private tablePrefs: TablePreferencesService
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      const pageNumber = parseInt(params['page'] || '1');
      const pageCount = PAGE_SIZE_OPTIONS.includes(parseInt(params['count'])) ? parseInt(params['count']) : DEFAULT_PAGE_SIZE;
      this.key = params['keyword'] ?? '';
      this.searchInput.setValue(this.key ?? '');
      if(params['filter'])
      {
        try {
          this.unparsedFilter = refillFilter(this.module, JSON.parse(decodeURIComponent(params['filter']))) as IInventoryFilter
          if(this.unparsedFilter)
            this.filter = parseFilterRequest(this.module, this.unparsedFilter);
        } catch (error) {
          this.filter == undefined
        }
      }
      this.page = { ...this.page, number: pageNumber, size: pageCount };
    });
  }

  ngOnInit(): void {
    this.initColumns();
    // Restore filter from URL on page refresh / direct load (or return from details)
    if (this.unparsedFilter || this.key) {
      this.store.dispatch(SetInventoryFilter({
        keyword: this.key ?? '',
        advanced: this.unparsedFilter ?? null
      }));
    }

    const sub0 = this.store
      .select((state) => state.products.productsSortBy)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((sort_by) => {
        this.activeSortBy = sort_by.key;
        this.activeSortMode = sort_by.mode;
        this.toPage(this.page);
      });
    // The filter has been changed. Reset the page number to 1.
    const sub1 = this.store
      .select((state) => state.products.productFilterState)
      .pipe(
        debounceTime(10),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
      )
      .subscribe((filter) => {
        this.unparsedFilter = filter.advanced;
        this.filter = parseFilterRequest(this.module, filter.advanced);
        this.key = filter.keyword;
        this.searchInput.setValue(this.key);
        this.toPage(this.page);
      });

    const sub2 = this.actions$.pipe(ofType(AddProductSuccess, UpdateProductSuccess, ImproveVariantsProductSuccess)).subscribe(() => {
      this.timestamp = new Date().getTime();
    });

    const sub3 = this.actions$.pipe(ofType(DeleteProductSuccess)).subscribe(() => {
      this.toPage(this.page);
    });


    const subHidden = this.store.select(selectHiddenFieldsForModule('product')).subscribe((hidden) => {
      this.hiddenProductFields = new Set(hidden);
      // Role visibility can arrive after init — rebuild the picker + grid order.
      this.pickerColumns = this.buildPickerColumns(this.gridColumnKeys);
      this.recomputeGridOrder();
    });

    this.subscriptions.push(sub0, sub1, sub2, sub3, subHidden);

    this.products$.subscribe(products => {
      this.selection.setRows(products);
      if(products.length > 0) {
        this.products = products;
        this.maxPerformanceProduct = products.reduce((prev, current) =>
          current.performance! > prev.performance! ? current : prev
        );
      }
    });
  }

  /**
   * True when the role does not hide the underlying field, regardless of the
   * user's column preference. Used both to gate the gear-menu toggles (so users
   * never see a toggle for a column they cannot reveal) and the columns themselves.
   */
  isColumnAllowedByRole(key: ListColumnKey): boolean {
    const fieldKey = this.COLUMN_TO_FIELD_KEY[key];
    if (!fieldKey) return true;
    return !this.hiddenProductFields.has(fieldKey);
  }

  isColumnVisible(key: ListColumnKey): boolean {
    if (!this.columns?.[key]?.visible) return false;
    return this.isColumnAllowedByRole(key);
  }

  /**
   * Fixed px width per grid-track column — feeds both the grid template and the
   * horizontal scroll. 'image' is absent: it shares the leading checkbox cell and
   * is not a grid track of its own.
   */
  private readonly COLUMN_WIDTHS: Partial<Record<ListColumnKey, string>> = {
    name: '240px',
    erp_id: '150px',
    sku: '140px',
    ean: '150px',
    brand: '150px',
    categories: '150px',
    variants: '110px',
    stock: '120px',
    catalogs: '110px',
    sales_price: '130px',
    performance: '170px',
    last_order: '140px',
    status: '120px',
  };

  // Default picker order. 'image' and 'name' are pinned to the front; 'image'
  // is hideable but not its own grid track (it shares the leading checkbox
  // cell), so it is never part of `gridColumnKeys`. 'name' cannot be hidden.
  // 'erp_id' sits before 'sku', mirroring the product details panel where the
  // ERP reference is listed above the SKU.
  private static readonly DEFAULT_ORDER: ListColumnKey[] = [
    'image', 'name', 'erp_id', 'sku', 'ean', 'brand', 'categories', 'variants',
    'stock', 'catalogs', 'sales_price', 'performance', 'last_order', 'status',
  ];

  /** Anchored to the front: immovable, nothing precedes them. */
  private static readonly PINNED_KEYS = new Set<ListColumnKey>(['image', 'name']);

  /**
   * Grid template for header + body + variant rows. The leading 100px track is the
   * checkbox/image cell. Tracks are emitted in the current column order, only for
   * visible columns, so the template matches the (CSS-`order`-placed) cells.
   */
  get gridTemplateColumns(): string {
    const cols: string[] = ['100px'];
    this.gridColumnKeys.forEach((key) => {
      if (this.isColumnVisible(key)) cols.push(this.COLUMN_WIDTHS[key] ?? '1fr');
    });
    // Trailing flexible filler: absorbs free space on wide screens (no extra cell
    // needed — grid auto-placement leaves this last track empty) and collapses to
    // 0 on narrow screens so the fixed columns overflow and the wrapper scrolls.
    cols.push('minmax(0, 1fr)');
    return cols.join(' ');
  }

  /** CSS `order` for a grid cell. Select/image cell = 0; data columns are 1-based. */
  columnOrder(key: ListColumnKey): number {
    return this.gridOrderIndex[key] ?? 0;
  }

  /** Hydrate column visibility + order from stored user preferences. */
  private initColumns(): void {
    const stored = this.tablePrefs.applyPreferences(
      ListProduitsComponent.THIS_TABLE_KEY,
      this.buildDefaultColumnSettings()
    );
    stored.forEach((column) => this.applyVisibility(column));
    this.gridColumnKeys = this.toGridKeys(stored.map((column) => column.key as ListColumnKey));
    this.pickerColumns = this.buildPickerColumns(this.gridColumnKeys);
    this.recomputeGridOrder();
  }

  /** Emitted by <ui-table-column-settings> on reorder / show / hide. */
  onColumnsChange(columns: ITableColumnSetting[]): void {
    columns.forEach((column) => this.applyVisibility(column));
    this.gridColumnKeys = this.toGridKeys(columns.map((column) => column.key as ListColumnKey));
    // Rebuild so 'image' is always listed first (it stays in the leading cell);
    // a re-shown image returns to the front rather than after the product name.
    this.pickerColumns = this.buildPickerColumns(this.gridColumnKeys);
    this.recomputeGridOrder();
    this.tablePrefs.save(ListProduitsComponent.THIS_TABLE_KEY, this.pickerColumns);
  }

  /**
   * Grid-track keys: drop 'image' (it lives in the select cell) and keep the
   * pinned 'name' column first regardless of any stale saved order.
   */
  private toGridKeys(keys: ListColumnKey[]): ListColumnKey[] {
    const grid = keys.filter((key) => key !== 'image' && key !== 'name');
    return ['name', ...grid];
  }

  private applyVisibility(column: ITableColumnSetting): void {
    const target = this.columns[column.key as ListColumnKey];
    if (target) target.visible = target.locked ? true : column.visible;
  }

  private buildDefaultColumnSettings(): ITableColumnSetting[] {
    return ListProduitsComponent.DEFAULT_ORDER.map((key) => this.toColumnSetting(key));
  }

  /** Picker contents: current order (+ image), role-allowed columns only. */
  private buildPickerColumns(gridKeys: ListColumnKey[]): ITableColumnSetting[] {
    const order: ListColumnKey[] = ['image', ...gridKeys];
    return order
      .filter((key) => this.isColumnAllowedByRole(key))
      .map((key) => this.toColumnSetting(key));
  }

  private toColumnSetting(key: ListColumnKey): ITableColumnSetting {
    return {
      key,
      label: this.columns[key].label,
      visible: this.columns[key].visible,
      pinned: ListProduitsComponent.PINNED_KEYS.has(key),
      // Only the product name is non-hideable; the image thumbnail may be hidden.
      hideable: key !== 'name',
    };
  }

  private recomputeGridOrder(): void {
    const index: Partial<Record<ListColumnKey, number>> = {};
    this.gridColumnKeys
      .filter((key) => this.isColumnVisible(key))
      .forEach((key, i) => (index[key] = i + 1));
    this.gridOrderIndex = index;
  }

  filters: any = {}
  search(event: any) {
    const key = event.target.value;

    if(this.filters.keyword != key){
      this.filters.keyword = key;
      this.store.dispatch(SetInventoryFilter({ keyword: key, advanced: this.filters.advanced ?? null }))
      let queryParams:any = {
        page: 1,
        count: this.page.size,
      }
      if(key != ''){
        queryParams.keyword = key;
      }
      else{
        queryParams.keyword = null;
      }
      this.router.navigate([],{
        relativeTo: this.navigationService.getCurrentRoute(),
        queryParams: {...queryParams},
        queryParamsHandling: 'merge'
      })
    }
  }

  setFilter() {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      minWidth: '650px',
      width: '90vw',
      height: '80vh',
    });
    dialogRef.afterClosed().subscribe((filterResult: any) => {
      if (filterResult) {
        this.page = { ...this.page, number: 1 };
        this.filters.advanced = filterResult;
        this.store.dispatch(SetInventoryFilter({
          keyword: this.key ?? '',
          advanced: filterResult
        }));
      }
    });
  }

  hasModuleFilters(): boolean {
    if(this.filter)
      return this.filter.filters && this.filter?.filters?.length > 0;
    return false;
  }

  clearFilter() {
    this.page = { ...this.page, number: 1 };
    let queryParams: any = { page: this.page.number, count: this.page.size, filter: null };
    if (this.key && this.key != '') {
      queryParams.keyword = this.key;
    }
    this.router.navigate([], { queryParams, queryParamsHandling: 'merge' });
    this.filters.advanced = refillFilter(this.module, null);
    this.unparsedFilter = null;
    this.filter = parseFilterRequest(this.module, this.filters.advanced);
    this.store.dispatch(SetInventoryFilter({
      keyword: this.key ?? '',
      advanced: this.filters.advanced
    }));
    this.store.dispatch(
      FetchProducts({
        page: { ...this.page },
        filter: this.filter,
        key: this.key,
        sort_by: this.activeSortBy,
        sort_mode: this.activeSortMode,
      })
    );
  }

  addProduct() {
    this.router.navigate(['add'],{
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
    });
  }

  getImage(image: any, attachment: boolean = false): string{
    return (attachment ? this.baseUrl.replace('/v1/', '') : this.baseUrl) + image + '?thumbnail=1&timestamp=' + this.timestamp
  }

  getAttachment(image: string): string{
    return this.baseUrl.replace('/v1/', '') + image
  }

  onImageError(imageUrl: string): void {
    this.failedImages.add(imageUrl); // Mark this image as failed
  }

  toPage(page: IPageCount) {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.store.dispatch(
      FetchProducts({ page: this.page, filter: this.filter, key: this.key, sort_by: this.activeSortBy, sort_mode: this.activeSortMode })
    );
    let filter:any = {}
    if(this.key && this.key != ''){
      filter.keyword = this.key;
    }
    filter.filter = encodeFilterObject(this.unparsedFilter);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };
    this.router.navigate([], {
      queryParams: queryParams
    });
  }

  toggleVariants(id: number) {
    this.expandedIndex = this.expandedIndex === id ? -1 : id;
  }

  isStockLow(product: IProduct): boolean {
    return product.is_owned === true && (product.stock?.quantity! <10 || product.stock?.quantity! < product.stock?.reorder_point!);
  }

  /**
   * Status badge helpers. Variants have no status of their own, so a variant row
   * shows its parent product's badge muted rather than an empty cell.
   */
  hasStatus(product: IProduct): boolean {
    return hasProductStatus(product.status);
  }

  statusTone(product: IProduct): StatusBadgeTone {
    return productStatusTone(product.status);
  }

  statusLabel(product: IProduct): string {
    return this.translateService.instant(productStatusI18nKey(product.status));
  }

  isExpired(product: IProduct): boolean {
  if (!product.expiry_date) return false;
  const today = new Date();
  const expiration = new Date(product.expiry_date);
  return expiration < today;
}


  editProduct(product?: IProduct){
    let filter:any = {}
    if(this.key && this.key != ''){
      filter.keyword = this.key;
    }
    filter.filter = encodeFilterObject(this.unparsedFilter);
    let queryParams = { page: this.page.number, count: this.page.size, ...filter };

    sessionStorage.setItem('returningFromInventoryDetails', 'true');

    this.router.navigate(['/inventory/', product!.id],{
      queryParams: queryParams,
    });
    this.store.dispatch(SetCurrentElementId({currentElementId: product!.id}));
  }

  delete(product: IProduct){
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent,{
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.store.dispatch(DeleteProduct({id: product.id}))
      }
    });
  }

  sortBy(key: string, mode: 'asc' | 'desc') {
    if(key == this.activeSortBy && mode == this.activeSortMode) return;

    this.activeSortBy = key;
    this.activeSortMode = mode;
    this.toPage(this.page)
  }

  exportProducts(): void {
    this.store.dispatch(exportProducts(
      { filter: parseInventoryFilterRequest(this.filters.advanced), key: this.key }
    ));
  }

  deleteSelectedProducts() {
    // Read the ids before anything can clear them: the success handler empties
    // the selection, and the dispatch below must not depend on when it runs.
    const productIds = this.selection.ids;

    const deleteSuccessSub = this.actions$.pipe(
      ofType(DeleteProductsSuccess),
      take(1)
    ).subscribe(() => {
      this.store.dispatch(FetchProducts({
        page: this.page,
        filter: this.filter,
        key: this.key,
        sort_by: this.activeSortBy,
        sort_mode: this.activeSortMode
      }));
      this.selection.clear();
      deleteSuccessSub.unsubscribe();
    });

    this.store.dispatch(DeleteProducts({ product_ids: productIds }));
  }

    deleteConfirmation() {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: 'assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.deleteSelectedProducts();
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub => sub.unsubscribe()));
    this.store.dispatch(SetInventorySortBy({ key: this.activeSortBy, mode: this.activeSortMode }));
  }
}
